import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { usePermissions } from "../../../PermissionsProvider";
import { TrainingMethod } from "../../../model_gen/finetuning";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Skeleton } from "../../ui/skeleton";
import { BarChart3Icon } from "lucide-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface TrainingMetricEntry {
  loss?: number;
  eval_loss?: number;
  learning_rate?: number;
  epoch?: number;
  step?: number;
  grad_norm?: number;
  eval_runtime?: number;
  eval_samples_per_second?: number;
  eval_steps_per_second?: number;
  // DPO-specific
  "rewards/chosen"?: number;
  "rewards/rejected"?: number;
  "rewards/margins"?: number;
  "rewards/accuracies"?: number;
  "logps/chosen"?: number;
  "logps/rejected"?: number;
  // GRPO-specific
  reward?: number;
  reward_std?: number;
  kl?: number;
}

interface TrainingMetricsChartProps {
  finetuneRunId: number;
  trainingMethod?: TrainingMethod;
}

const emaSmooth = (values: number[], alpha = 0.98): (number | null)[] => {
  if (values.length === 0) return [];
  let ema = values[0];
  return values.map((v, i) => {
    if (v === null || v === undefined) return null;
    ema = i === 0 ? v : alpha * ema + (1 - alpha) * v;
    return ema;
  });
};

function buildLineChartOptions(yLabel: string, tooltipFormat?: (val: number) => string) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: "index" as const },
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          color: "#737373",
          font: { size: 12 },
          usePointStyle: true,
          pointStyle: "line",
          filter: (item: any) => !item.text.includes("(raw)"),
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "#3b82f6",
        borderWidth: 1,
        cornerRadius: 6,
        filter: (item: any) => item.parsed.y !== null,
        callbacks: {
          title: (context: any) => `Step ${context[0].label}`,
          label: (context: any) => {
            const val = context.parsed.y;
            if (val === null || val === undefined) return "";
            const formatted = tooltipFormat
              ? tooltipFormat(val)
              : val.toFixed(4);
            return `${context.dataset.label}: ${formatted}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Training Step", color: "#737373" },
        grid: { display: false },
        ticks: { color: "#737373", font: { size: 11 }, maxTicksLimit: 10 },
      },
      y: {
        title: { display: true, text: yLabel, color: "#737373" },
        grid: { color: "rgba(115, 115, 115, 0.1)" },
        ticks: { color: "#737373", font: { size: 11 } },
      },
    },
  };
}

function buildSmoothedDataset(
  label: string,
  values: number[],
  color: string,
  showRaw = true
) {
  const datasets: any[] = [];
  if (showRaw) {
    datasets.push({
      label: `${label} (raw)`,
      data: values,
      borderColor: color.replace(")", ", 0.15)").replace("rgb", "rgba"),
      borderWidth: 1,
      fill: false,
      tension: 0.3,
      pointRadius: 0,
      pointHoverRadius: 3,
      order: 2,
    });
  }
  datasets.push({
    label,
    data: emaSmooth(values),
    borderColor: color,
    borderWidth: 2,
    fill: false,
    tension: 0.4,
    pointRadius: 0,
    pointHoverRadius: 4,
    pointHoverBackgroundColor: color,
    pointHoverBorderColor: "#ffffff",
    pointHoverBorderWidth: 2,
    order: 1,
  });
  return datasets;
}

function buildSimpleDataset(label: string, values: number[], color: string, fill = false) {
  return {
    label,
    data: values,
    borderColor: color,
    backgroundColor: fill ? color.replace(")", ", 0.1)").replace("rgb", "rgba") : undefined,
    borderWidth: 2,
    fill,
    tension: 0.3,
    pointRadius: 0,
    pointHoverRadius: 4,
  };
}

// ─── Summary Card ─────────────────────────────────────────────────────────────
const SummaryCard: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Card className="border-border">
    <CardContent className="p-4">
      <p className="text-sm text-muted-foreground font-gc-reg">{label}</p>
      <p className="text-2xl font-gc-med text-foreground">{value}</p>
    </CardContent>
  </Card>
);

// ─── Chart Card ───────────────────────────────────────────────────────────────
const ChartCard: React.FC<{
  title: string;
  height?: string;
  data: any;
  options: any;
}> = ({ title, height = "300px", data, options }) => (
  <Card className="border-border">
    <CardHeader className="pb-4">
      <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
        <BarChart3Icon className="w-5 h-5 text-maitai-green" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div style={{ height }}>
        <Line data={data} options={options} />
      </div>
    </CardContent>
  </Card>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const TrainingMetricsChart: React.FC<TrainingMetricsChartProps> = ({
  finetuneRunId,
  trainingMethod,
}) => {
  const { apiKey } = usePermissions();
  const [metrics, setMetrics] = useState<TrainingMetricEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!finetuneRunId) return;

    const sub = FinetuneRunService.getTrainingMetrics(
      finetuneRunId,
      setLoading,
      apiKey
    ).subscribe(
      (data: TrainingMetricEntry[]) => {
        setMetrics(data || []);
      },
      (error: any) => {
        console.error("Failed to load training metrics:", error);
        setMetrics([]);
        setLoading(false);
      }
    );
    return () => sub.unsubscribe();
  }, [finetuneRunId, apiKey]);

  const trainEntries = useMemo(
    () => metrics.filter((e) => e.loss !== undefined && e.step !== undefined),
    [metrics]
  );

  const evalEntries = useMemo(
    () => metrics.filter((e) => e.eval_loss !== undefined),
    [metrics]
  );

  // Detect which method-specific metrics are present in the data
  const detectedMethod = useMemo(() => {
    if (trainingMethod) return trainingMethod;
    const hasRewardsMargins = metrics.some((e) => e["rewards/margins"] !== undefined);
    if (hasRewardsMargins) return "DPO" as TrainingMethod;
    const hasReward = metrics.some((e) => e.reward !== undefined);
    if (hasReward) return "GRPO" as TrainingMethod;
    return "SFT" as TrainingMethod;
  }, [metrics, trainingMethod]);

  // ─── Loss Chart ─────────────────────────────────────────────────────────────
  const lossChartData = useMemo(() => {
    if (trainEntries.length === 0) return null;

    const labels = trainEntries.map((e) => String(e.step));
    const stepToIndex = new Map<number, number>();
    trainEntries.forEach((e, i) => stepToIndex.set(e.step!, i));

    const datasets: any[] = buildSmoothedDataset(
      "Train Loss",
      trainEntries.map((e) => e.loss!),
      "#3b82f6"
    );

    if (evalEntries.length > 0) {
      const evalData: (number | null)[] = new Array(labels.length).fill(null);
      const evalWithStep = evalEntries.filter((e) => e.step !== undefined);
      if (evalWithStep.length > 0) {
        for (const e of evalWithStep) {
          const idx = stepToIndex.get(e.step!);
          if (idx !== undefined) {
            evalData[idx] = e.eval_loss!;
          } else {
            let closestIdx = 0;
            let closestDist = Infinity;
            stepToIndex.forEach((i, step) => {
              const dist = Math.abs(step - e.step!);
              if (dist < closestDist) {
                closestDist = dist;
                closestIdx = i;
              }
            });
            evalData[closestIdx] = e.eval_loss!;
          }
        }
      } else {
        for (const e of evalEntries) {
          let bestIdx = 0;
          let bestDist = Infinity;
          for (let i = 0; i < trainEntries.length; i++) {
            const dist = Math.abs(
              (trainEntries[i].epoch || 0) - (e.epoch || 0)
            );
            if (dist < bestDist) {
              bestDist = dist;
              bestIdx = i;
            }
          }
          evalData[bestIdx] = e.eval_loss!;
        }
      }
      datasets.push({
        label: "Eval Loss",
        data: evalData,
        borderColor: "#f59e0b",
        backgroundColor: "rgba(245, 158, 11, 0.1)",
        borderWidth: 2.5,
        fill: false,
        tension: 0.3,
        spanGaps: true,
        pointRadius: evalData.map((v) => (v !== null ? 5 : 0)),
        pointBackgroundColor: "#f59e0b",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        pointHoverRadius: 7,
        order: 0,
      });
    }

    return { labels, datasets };
  }, [trainEntries, evalEntries]);

  // ─── Learning Rate Chart ────────────────────────────────────────────────────
  const lrChartData = useMemo(() => {
    const lrEntries = trainEntries.filter((e) => e.learning_rate !== undefined);
    if (lrEntries.length === 0) return null;
    return {
      labels: lrEntries.map((e) => String(e.step)),
      datasets: [
        buildSimpleDataset(
          "Learning Rate",
          lrEntries.map((e) => e.learning_rate!),
          "#21b892",
          true
        ),
      ],
    };
  }, [trainEntries]);

  // ─── DPO: Rewards Chart ────────────────────────────────────────────────────
  const dpoRewardsChartData = useMemo(() => {
    if (detectedMethod !== "DPO") return null;
    const entries = metrics.filter(
      (e) => e["rewards/chosen"] !== undefined && e.step !== undefined
    );
    if (entries.length === 0) return null;
    return {
      labels: entries.map((e) => String(e.step)),
      datasets: [
        buildSimpleDataset(
          "Chosen Reward",
          entries.map((e) => e["rewards/chosen"]!),
          "#22c55e"
        ),
        buildSimpleDataset(
          "Rejected Reward",
          entries.map((e) => e["rewards/rejected"]!),
          "#ef4444"
        ),
      ],
    };
  }, [metrics, detectedMethod]);

  // ─── DPO: Reward Margin Chart ──────────────────────────────────────────────
  const dpoMarginChartData = useMemo(() => {
    if (detectedMethod !== "DPO") return null;
    const entries = metrics.filter(
      (e) => e["rewards/margins"] !== undefined && e.step !== undefined
    );
    if (entries.length === 0) return null;
    return {
      labels: entries.map((e) => String(e.step)),
      datasets: buildSmoothedDataset(
        "Reward Margin",
        entries.map((e) => e["rewards/margins"]!),
        "#8b5cf6"
      ),
    };
  }, [metrics, detectedMethod]);

  // ─── DPO: Accuracy Chart ───────────────────────────────────────────────────
  const dpoAccuracyChartData = useMemo(() => {
    if (detectedMethod !== "DPO") return null;
    const entries = metrics.filter(
      (e) => e["rewards/accuracies"] !== undefined && e.step !== undefined
    );
    if (entries.length === 0) return null;
    return {
      labels: entries.map((e) => String(e.step)),
      datasets: buildSmoothedDataset(
        "Accuracy",
        entries.map((e) => e["rewards/accuracies"]!),
        "#0ea5e9"
      ),
    };
  }, [metrics, detectedMethod]);

  // ─── GRPO: Reward Chart ────────────────────────────────────────────────────
  const grpoRewardChartData = useMemo(() => {
    if (detectedMethod !== "GRPO") return null;
    const entries = metrics.filter(
      (e) => e.reward !== undefined && e.step !== undefined
    );
    if (entries.length === 0) return null;

    const datasets = buildSmoothedDataset(
      "Mean Reward",
      entries.map((e) => e.reward!),
      "#22c55e"
    );

    const hasStd = entries.some((e) => e.reward_std !== undefined);
    if (hasStd) {
      datasets.push(
        buildSimpleDataset(
          "Reward Std Dev",
          entries.map((e) => e.reward_std ?? 0),
          "#f59e0b"
        )
      );
    }

    return {
      labels: entries.map((e) => String(e.step)),
      datasets,
    };
  }, [metrics, detectedMethod]);

  // ─── GRPO: KL Divergence Chart ─────────────────────────────────────────────
  const grpoKlChartData = useMemo(() => {
    if (detectedMethod !== "GRPO") return null;
    const entries = metrics.filter(
      (e) => e.kl !== undefined && e.step !== undefined
    );
    if (entries.length === 0) return null;
    return {
      labels: entries.map((e) => String(e.step)),
      datasets: buildSmoothedDataset(
        "KL Divergence",
        entries.map((e) => e.kl!),
        "#f97316"
      ),
    };
  }, [metrics, detectedMethod]);

  // ─── Chart Options ─────────────────────────────────────────────────────────
  const lossOptions = useMemo(() => buildLineChartOptions("Loss"), []);
  const lrOptions = useMemo(
    () => buildLineChartOptions("Learning Rate", (v) => v.toExponential(2)),
    []
  );
  const rewardsOptions = useMemo(() => buildLineChartOptions("Reward"), []);
  const marginOptions = useMemo(
    () => buildLineChartOptions("Margin (chosen - rejected)"),
    []
  );
  const accuracyOptions = useMemo(
    () => buildLineChartOptions("Accuracy", (v) => `${(v * 100).toFixed(1)}%`),
    []
  );
  const grpoRewardOptions = useMemo(() => buildLineChartOptions("Reward"), []);
  const klOptions = useMemo(() => buildLineChartOptions("KL Divergence"), []);

  // ─── Loading / Empty States ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="space-y-6">
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <BarChart3Icon className="w-5 h-5 text-maitai-green" />
              Training Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] flex items-center justify-center">
              <div className="space-y-3 w-full">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-32 w-full" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!metrics || metrics.length === 0 || !lossChartData) {
    return (
      <div className="space-y-6">
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <BarChart3Icon className="w-5 h-5 text-maitai-green" />
              Training Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px] flex items-center justify-center">
              <div className="text-center space-y-3">
                <h3 className="text-lg font-gc-med text-foreground">
                  No training metrics available
                </h3>
                <p className="text-muted-foreground max-w-md">
                  Training metrics will appear here once a training run
                  completes. Metrics are recorded during training and include
                  loss curves and learning rate schedules.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Summary Stats ──────────────────────────────────────────────────────────
  const finalTrainLoss = trainEntries[trainEntries.length - 1]?.loss;
  const finalEvalLoss =
    evalEntries.length > 0
      ? evalEntries[evalEntries.length - 1]?.eval_loss
      : null;
  const totalSteps = trainEntries[trainEntries.length - 1]?.step || 0;

  // DPO-specific final values
  const dpoAccEntries = metrics.filter(
    (e) => e["rewards/accuracies"] !== undefined
  );
  const finalDpoAccuracy =
    dpoAccEntries.length > 0
      ? dpoAccEntries[dpoAccEntries.length - 1]["rewards/accuracies"]
      : null;
  const dpoMarginEntries = metrics.filter(
    (e) => e["rewards/margins"] !== undefined
  );
  const finalDpoMargin =
    dpoMarginEntries.length > 0
      ? dpoMarginEntries[dpoMarginEntries.length - 1]["rewards/margins"]
      : null;

  // GRPO-specific final values
  const grpoRewardEntries = metrics.filter((e) => e.reward !== undefined);
  const finalGrpoReward =
    grpoRewardEntries.length > 0
      ? grpoRewardEntries[grpoRewardEntries.length - 1].reward
      : null;
  const grpoKlEntries = metrics.filter((e) => e.kl !== undefined);
  const finalGrpoKl =
    grpoKlEntries.length > 0
      ? grpoKlEntries[grpoKlEntries.length - 1].kl
      : null;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          label="Final Train Loss"
          value={finalTrainLoss?.toFixed(4) || "-"}
        />
        <SummaryCard
          label="Final Eval Loss"
          value={finalEvalLoss?.toFixed(4) || "-"}
        />
        <SummaryCard label="Total Steps" value={String(totalSteps)} />

        {detectedMethod === "DPO" && finalDpoAccuracy !== null && (
          <SummaryCard
            label="Final DPO Accuracy"
            value={
              finalDpoAccuracy !== undefined && finalDpoAccuracy !== null
                ? `${(finalDpoAccuracy * 100).toFixed(1)}%`
                : "-"
            }
          />
        )}
        {detectedMethod === "DPO" && finalDpoMargin !== null && (
          <SummaryCard
            label="Final Reward Margin"
            value={finalDpoMargin?.toFixed(4) || "-"}
          />
        )}
        {detectedMethod === "GRPO" && finalGrpoReward !== null && (
          <SummaryCard
            label="Final Mean Reward"
            value={
              finalGrpoReward !== undefined && finalGrpoReward !== null
                ? finalGrpoReward.toFixed(4)
                : "-"
            }
          />
        )}
        {detectedMethod === "GRPO" && finalGrpoKl !== null && (
          <SummaryCard
            label="Final KL Divergence"
            value={finalGrpoKl?.toFixed(4) || "-"}
          />
        )}
      </div>

      {/* Loss Chart (always shown) */}
      <ChartCard title="Loss" data={lossChartData} options={lossOptions} />

      {/* DPO-specific charts */}
      {detectedMethod === "DPO" && dpoRewardsChartData && (
        <ChartCard
          title="Rewards (Chosen vs Rejected)"
          data={dpoRewardsChartData}
          options={rewardsOptions}
        />
      )}
      {detectedMethod === "DPO" && dpoMarginChartData && (
        <ChartCard
          title="Reward Margin"
          data={dpoMarginChartData}
          options={marginOptions}
        />
      )}
      {detectedMethod === "DPO" && dpoAccuracyChartData && (
        <ChartCard
          title="DPO Accuracy"
          data={dpoAccuracyChartData}
          options={accuracyOptions}
        />
      )}

      {/* GRPO-specific charts */}
      {detectedMethod === "GRPO" && grpoRewardChartData && (
        <ChartCard
          title="Mean Reward"
          data={grpoRewardChartData}
          options={grpoRewardOptions}
        />
      )}
      {detectedMethod === "GRPO" && grpoKlChartData && (
        <ChartCard
          title="KL Divergence"
          data={grpoKlChartData}
          options={klOptions}
        />
      )}

      {/* Learning Rate Chart (always shown) */}
      {lrChartData && (
        <ChartCard
          title="Learning Rate Schedule"
          height="200px"
          data={lrChartData}
          options={lrOptions}
        />
      )}
    </div>
  );
};

export default TrainingMetricsChart;
